/**
 * The vendor console's sections' frame (container, phase 7b; D39): the section's page, shown only to a caller holding
 * `vendor.ops` or higher tenant-wide in the staff tenant (`vendorAccessOf`). Since D39 the sections live in the staff
 * console (`views/console/ConsoleLayout.tsx`), whose top bar is their navigation; the customer app has no Vendor tab.
 * Everyone else gets a plain "not available" (the server refuses every vendor route regardless).
 */
import { Outlet } from '@tanstack/react-router';
import { useGrants, useMe } from '../app/hooks.js';
import { CloudOnlyGate } from './CloudOnlyPage.js';
import { vendorAccessOf } from '../lib/vendor.js';

/** A cloud-only screen (`RouteDef.hosts`): its calls go to the cloud; "needs a cloud connection" where they cannot. */
export function VendorLayout() {
  return (
    <CloudOnlyGate route="getVendorOps" what="The staff console" path="/vendor">
      <VendorLayoutPage />
    </CloudOnlyGate>
  );
}

function VendorLayoutPage() {
  const me = useMe();
  const access = vendorAccessOf(useGrants());
  if (me.isPending) return <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>;
  if (!access.canSee) {
    return (
      <p className="mx-auto max-w-xl p-8 text-center text-sm text-muted-foreground" data-testid="vendor-unavailable">
        The staff console’s sections need vendor.ops (or a higher staff level) in the staff tenant.
      </p>
    );
  }
  return (
    <div className="mx-auto grid max-w-7xl gap-6" data-testid="vendor-area">
      <Outlet />
    </div>
  );
}

/** The write controls' gate, for the section pages. */
export function useVendorAdmin(): boolean {
  return vendorAccessOf(useGrants()).canAdmin;
}
